import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CAT_COLORS = {
  ecole: '#2196F3', sport: '#4CAF50', medical: '#F44336',
  famille: '#9C27B0', admin: '#FF9800', autre: '#6C63FF'
};

const CAT_ICONS = { ecole: '🏫', sport: '⚽', medical: '💊', famille: '👨‍👩‍👧', admin: '📄', autre: '📌' };

// L'API renvoie les dates en "YYYY-MM-DD HH:MM:SS" (sans fuseau, mysql2 dateStrings:true). Ce
// format n'est pas garanti par la norme ISO (contrairement à "YYYY-MM-DDTHH:MM:SS") : selon le
// navigateur, il peut être interprété comme UTC au lieu d'heure locale, décalant l'affichage de
// +/-1 ou 2h (bug constaté le 13/09/2026 : un événement à 19h s'affichait à 17h). On force le
// séparateur 'T' pour que tous les moteurs JS le traitent comme une heure locale.
function parseApiDateTime(dt) {
  if (!dt) return null;
  return new Date(String(dt).replace(' ', 'T'));
}

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toLocalISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function ExceptionPicker({ eventId, holidays, onDone }) {
  const [mode, setMode] = useState('date'); // 'date' | 'vacances' | 'ferie' | 'toutes'
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [saving, setSaving] = useState(false);

  const today = toLocalISO(new Date());
  const futureSchool = holidays.school_holidays.filter(h => h.end_date >= today);
  const futurePub = holidays.public_holidays.filter(h => h.date >= today);

  const datesInRange = (start, end) => {
    const dates = [];
    let d = new Date(start + 'T00:00');
    const e = new Date(end + 'T00:00');
    while (d <= e) { dates.push(toLocalISO(d)); d.setDate(d.getDate()+1); }
    return dates;
  };

  const save = async () => {
    setSaving(true);
    let dates = [];
    let reason = '';
    if (mode === 'date' && selectedDate) { dates = [selectedDate]; reason = 'Date spécifique'; }
    else if (mode === 'vacances' && selectedHoliday) { dates = datesInRange(selectedHoliday.start_date, selectedHoliday.end_date); reason = selectedHoliday.name; }
    else if (mode === 'ferie' && selectedHoliday) { dates = [selectedHoliday.date]; reason = selectedHoliday.name; }
    else if (mode === 'toutes') {
      const seen = new Set();
      futureSchool.forEach(h => datesInRange(h.start_date, h.end_date).forEach(d => seen.add(d)));
      dates = [...seen].sort();
      reason = 'Toutes vacances scolaires Zone A';
    }
    if (dates.length) {
      await api.post(`/events/${eventId}/exceptions`, { dates, reason });
    }
    setSaving(false);
    onDone();
  };

  const allDaysCount = (() => {
    const seen = new Set();
    futureSchool.forEach(h => datesInRange(h.start_date, h.end_date).forEach(d => seen.add(d)));
    return seen.size;
  })();

  return (
    <div style={{ marginTop: 8, padding: '10px 12px', background: '#EEF0FF', borderRadius: 10 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[['date','📅 Date'], ['vacances','🏖️ Vacances'], ['ferie','🇫🇷 Férié'], ['toutes','🗓️ Toutes les vacances']].map(([v,l]) => (
          <button key={v} type="button" onClick={() => { setMode(v); setSelectedHoliday(null); setSelectedDate(''); }}
            style={{ flex: v==='toutes'?'1 1 100%':1, padding:'6px 4px', borderRadius:8, border: mode===v?'2px solid #6C63FF':'2px solid #E0E4F0', background: mode===v?'#6C63FF':'white', color: mode===v?'white':'#555', fontSize:11, fontWeight:700, cursor:'pointer' }}>
            {l}
          </button>
        ))}
      </div>
      {mode === 'date' && (
        <input type="date" className="form-input" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} min={today} />
      )}
      {mode === 'vacances' && (
        <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
          {futureSchool.map(h => (
            <button key={h.id} type="button" onClick={() => setSelectedHoliday(h)}
              style={{ padding:'8px 10px', borderRadius:8, border: selectedHoliday?.id===h.id?'2px solid #FF9800':'2px solid #E0E4F0', background: selectedHoliday?.id===h.id?'#FFF3E0':'white', textAlign:'left', cursor:'pointer', fontSize:12 }}>
              <strong>{h.name}</strong> · {h.start_date} → {h.end_date}
            </button>
          ))}
          {futureSchool.length === 0 && <div style={{fontSize:12,color:'#888'}}>Pas de vacances à venir</div>}
        </div>
      )}
      {mode === 'ferie' && (
        <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
          {futurePub.map(h => (
            <button key={h.id} type="button" onClick={() => setSelectedHoliday(h)}
              style={{ padding:'8px 10px', borderRadius:8, border: selectedHoliday?.id===h.id?'2px solid #E91E63':'2px solid #E0E4F0', background: selectedHoliday?.id===h.id?'#FCE4EC':'white', textAlign:'left', cursor:'pointer', fontSize:12 }}>
              <strong>{h.name}</strong> · {h.date}
            </button>
          ))}
        </div>
      )}
      {mode === 'toutes' && (
        <div style={{ padding:'10px', background:'white', borderRadius:8, fontSize:12, color:'#555' }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>🗓️ Toutes les vacances scolaires Zone A</div>
          <div style={{ display:'flex', flexDirection:'column', gap:3, maxHeight:140, overflowY:'auto' }}>
            {futureSchool.map(h => (
              <div key={h.id} style={{ color:'#777' }}>• {h.name} : {h.start_date} → {h.end_date}</div>
            ))}
          </div>
          <div style={{ marginTop:8, color:'#6C63FF', fontWeight:700 }}>{allDaysCount} jours à bloquer</div>
        </div>
      )}
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <button type="button" onClick={onDone} className="btn btn-ghost btn-sm" style={{flex:1}}>Annuler</button>
        <button type="button" onClick={save} disabled={saving || (mode !== 'toutes' && !selectedDate && !selectedHoliday)} className="btn btn-primary btn-sm" style={{flex:2}}>
          {saving ? '...' : '✓ Confirmer'}
        </button>
      </div>
    </div>
  );
}

export default function Calendar() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState({ school_holidays: [], public_holidays: [] });
  const [exceptions, setExceptions] = useState([]);
  const [showExceptions, setShowExceptions] = useState(false);
  const [addingException, setAddingException] = useState(false);

  const [form, setForm] = useState({
    title: '', start_datetime: '', end_datetime: '', all_day: false,
    user_id: '', category: 'autre', location: '', description: '', recurrence: 'aucune'
  });

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  const load = useCallback(() => {
    const start = toLocalISO(weekStart);
    const end = toLocalISO(weekEnd);
    api.get(`/events?start=${start}&end=${end}`).then(r => setEvents(r.data)).catch(console.error);
    api.get('/family/members').then(r => setMembers(r.data)).catch(console.error);
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener('family_refresh', load); return () => window.removeEventListener('family_refresh', load); }, [load]);
  useEffect(() => {
    api.get('/holidays').then(r => setHolidays(r.data)).catch(console.error);
  }, []);

  const today = toLocalISO(new Date());

  const isSchoolHoliday = (dateStr) => holidays.school_holidays.some(h => dateStr >= h.start_date && dateStr <= h.end_date);
  const isPublicHoliday = (dateStr) => holidays.public_holidays.find(h => h.date === dateStr);
  const getHolidayName = (dateStr) => {
    const sh = holidays.school_holidays.find(h => dateStr >= h.start_date && dateStr <= h.end_date);
    if (sh) return sh.name;
    const ph = holidays.public_holidays.find(h => h.date === dateStr);
    if (ph) return ph.name;
    return null;
  };

  const openNew = (date) => {
    const d = toLocalISO(typeof date === 'string' ? new Date(date + 'T00:00') : date);
    setForm({ title: '', start_datetime: `${d}T08:00`, end_datetime: '', all_day: false, user_id: '', category: 'autre', location: '', description: '', recurrence: 'aucune' });
    setEditEvent(null);
    setShowForm(true);
  };

  const openEdit = (ev) => {
    const startDT = parseApiDateTime(ev.start_datetime);
    const pad = n => String(n).padStart(2, '0');
    const toInput = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({
      title: ev.title, category: ev.category || 'autre', location: ev.location || '',
      description: ev.description || '', user_id: ev.user_id || '',
      start_datetime: toInput(startDT), all_day: ev.all_day,
      end_datetime: ev.end_datetime ? toInput(parseApiDateTime(ev.end_datetime)) : '',
      recurrence: ev.recurrence || 'aucune'
    });
    setEditEvent(ev);
    setShowForm(true);
    if (ev.recurrence && ev.recurrence !== 'aucune') {
      api.get(`/events/${ev.id}/exceptions`).then(r => setExceptions(r.data)).catch(console.error);
    } else {
      setExceptions([]);
    }
    setShowExceptions(false);
    setAddingException(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_datetime: form.start_datetime.replace('T', ' ') + ':00',
        end_datetime: form.end_datetime ? form.end_datetime.replace('T', ' ') + ':00' : null,
        user_id: form.user_id || null,
      };
      if (editEvent) await api.put(`/events/${editEvent.id}`, payload);
      else await api.post('/events', payload);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editEvent || !window.confirm('Supprimer cet événement ?')) return;
    await api.delete(`/events/${editEvent.id}`);
    setShowForm(false);
    load();
  };

  const deleteOccurrence = async (ev, e) => {
    e.stopPropagation();
    const isRecurring = ev.recurrence && ev.recurrence !== 'aucune';
    const label = isRecurring ? 'Supprimer cette occurrence ?' : 'Supprimer cet événement ?';
    if (!window.confirm(label)) return;
    if (isRecurring && ev.occurrence_date) {
      await api.post(`/events/${ev.id}/exceptions`, { dates: [ev.occurrence_date], reason: 'Supprimé' });
    } else {
      await api.delete(`/events/${ev.id}`);
    }
    load();
  };

  const eventsForDay = (dateStr) => events.filter(ev => {
    const dt = parseApiDateTime(ev.start_datetime);
    const d = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    return d === dateStr;
  });

  const weekLabel = (() => {
    const start = weekStart;
    const end = weekEnd;
    if (start.getMonth() === end.getMonth()) return `${start.getDate()}–${end.getDate()} ${MONTHS_FR[start.getMonth()]} ${start.getFullYear()}`;
    return `${start.getDate()} ${MONTHS_FR[start.getMonth()]} – ${end.getDate()} ${MONTHS_FR[end.getMonth()]} ${end.getFullYear()}`;
  })();

  return (
    <div>
      <div className="topbar">
        <div className="page-title">📅 Agenda</div>
        <button className="btn btn-primary btn-sm" onClick={() => openNew(new Date())}>+ Événement</button>
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        {/* Navigation semaine */}
        <div className="week-nav">
          <button className="btn-icon btn" onClick={() => setWeekStart(addDays(weekStart, -7))}>‹</button>
          <div>
            <div className="week-label">{weekLabel}</div>
            <div style={{ textAlign: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(getMonday(new Date()))} style={{ fontSize: 11, padding: '2px 10px' }}>Aujourd'hui</button>
            </div>
          </div>
          <button className="btn-icon btn" onClick={() => setWeekStart(addDays(weekStart, 7))}>›</button>
        </div>

        {/* Grille semaine */}
        <div className="week-days">
          {weekDays.map((day, i) => {
            const dateStr = toLocalISO(day);
            const dayEvents = eventsForDay(dateStr);
            const isToday = dateStr === today;
            const isSelected = selectedDay === dateStr;
            return (
              <div key={i} className="day-col"
                style={{ background: isSchoolHoliday(dateStr) ? '#FFF8E1' : isPublicHoliday(dateStr) ? '#FCE4EC' : undefined }}
                onClick={() => setSelectedDay(isSelected ? null : dateStr)}>
                <div className="day-header">
                  <div className="day-name">{DAYS_FR[i]}</div>
                  <div className={`day-num${isToday ? ' today' : ''}${isSelected ? ' selected-day' : ''}`}>
                    {day.getDate()}
                  </div>
                  {isSchoolHoliday(dateStr) && <div style={{ fontSize: 7, color: '#FF9800', fontWeight: 700, lineHeight: 1 }}>VACS</div>}
                  {isPublicHoliday(dateStr) && <div style={{ fontSize: 7, color: '#E91E63', fontWeight: 700, lineHeight: 1 }}>FÉRIÉ</div>}
                </div>
                <div className="day-events">
                  {dayEvents.slice(0, 2).map(ev => (
                    <div key={ev.id} className="cal-event"
                      style={{ background: (CAT_COLORS[ev.category] || '#6C63FF') + '22', color: CAT_COLORS[ev.category] || '#6C63FF' }}
                      onClick={e => { e.stopPropagation(); openEdit(ev); }}
                      title={ev.title}>
                      {!ev.all_day && <span style={{ fontWeight: 400 }}>{parseApiDateTime(ev.start_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} </span>}
                      {ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && <div style={{ fontSize: 9, color: '#8888AA', textAlign: 'center' }}>+{dayEvents.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* PANNEAU JOUR — style Outlook */}
        {selectedDay && (() => {
          const dayEvents = eventsForDay(selectedDay);
          const dayDate = new Date(selectedDay + 'T00:00');
          const isToday = selectedDay === today;
          return (
            <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 4px 24px rgba(108,99,255,0.13)', margin: '12px 0', overflow: 'hidden' }}>
              {/* En-tête du jour */}
              <div style={{ background: 'linear-gradient(135deg, #6C63FF, #8B80FF)', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {isToday ? '⭐ Aujourd\'hui' : dayDate.toLocaleDateString('fr-FR', { weekday: 'long' })}
                  </div>
                  <div style={{ color: 'white', fontSize: 20, fontWeight: 800 }}>
                    {dayDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={() => openNew(selectedDay)}
                    style={{ background: 'white', color: '#6C63FF', border: 'none', borderRadius: 10, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    + Ajouter
                  </button>
                  <button onClick={() => setSelectedDay(null)}
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ×
                  </button>
                </div>
              </div>

              {/* Événements du jour */}
              <div style={{ padding: '12px 16px' }}>
                {dayEvents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#AAAACC' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Aucun événement ce jour</div>
                    <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => openNew(selectedDay)}>
                      + Créer un événement
                    </button>
                  </div>
                ) : (
                  dayEvents
                    .sort((a, b) => parseApiDateTime(a.start_datetime) - parseApiDateTime(b.start_datetime))
                    .map(ev => {
                      const color = CAT_COLORS[ev.category] || '#6C63FF';
                      return (
                        <div key={ev.id} onClick={() => openEdit(ev)} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 12, marginBottom: 8, cursor: 'pointer', background: color + '0F', borderLeft: `4px solid ${color}`, transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = color + '20'}
                          onMouseLeave={e => e.currentTarget.style.background = color + '0F'}>
                          <div style={{ fontSize: 22, lineHeight: 1 }}>{CAT_ICONS[ev.category] || '📌'}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#333355', marginBottom: 2 }}>{ev.title}</div>
                            <div style={{ fontSize: 12, color: '#8888AA', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                              {ev.all_day
                                ? <span>🕐 Toute la journée</span>
                                : <span>🕐 {parseApiDateTime(ev.start_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}{ev.end_datetime ? ` → ${parseApiDateTime(ev.end_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                              }
                              {ev.location && <span>📍 {ev.location}</span>}
                              {ev.avatar_emoji && <span>{ev.avatar_emoji} {ev.user_name || ''}</span>}
                            </div>
                            {ev.description && <div style={{ fontSize: 12, color: '#AAAACC', marginTop: 4, fontStyle: 'italic' }}>{ev.description}</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, alignSelf: 'center' }}>
                            <button onClick={e => deleteOccurrence(ev, e)}
                              title={ev.recurrence && ev.recurrence !== 'aucune' ? 'Supprimer cette occurrence' : 'Supprimer'}
                              style={{ background: 'none', border: 'none', color: '#CCAAAA', fontSize: 15, cursor: 'pointer', padding: '2px 4px', borderRadius: 6, lineHeight: 1 }}>🗑️</button>
                            <div style={{ color: '#CCCCDD', fontSize: 18 }}>›</div>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          );
        })()}

        {/* Liste semaine compacte quand aucun jour sélectionné */}
        {!selectedDay && (
          <>
            <div className="section-title" style={{ marginTop: 16 }}>📋 Cette semaine</div>
            {weekDays.every(day => eventsForDay(toLocalISO(day)).length === 0) ? (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <div className="empty-text">Aucun événement cette semaine<br /><span style={{ fontSize: 12 }}>Cliquez sur un jour pour voir le détail</span></div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => openNew(new Date())}>+ Ajouter un événement</button>
              </div>
            ) : (
              weekDays.map((day, i) => {
                const dateStr = toLocalISO(day);
                const dayEvents = eventsForDay(dateStr);
                if (!dayEvents.length) return null;
                const isToday = dateStr === today;
                return (
                  <div key={i} style={{ marginBottom: 8, cursor: 'pointer' }} onClick={() => setSelectedDay(dateStr)}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: isToday ? '#6C63FF' : '#8888AA', textTransform: 'capitalize', marginBottom: 4 }}>
                      {isToday ? '⭐ ' : ''}{day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} ({dayEvents.length})
                    </div>
                    {dayEvents.map(ev => (
                      <div key={ev.id} className="card-sm" style={{ borderLeft: `4px solid ${CAT_COLORS[ev.category] || '#6C63FF'}`, marginBottom: 6 }}
                        onClick={e => { e.stopPropagation(); openEdit(ev); }}>
                        <div className="flex-between">
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{CAT_ICONS[ev.category] || '📌'} {ev.title}</div>
                            <div className="text-muted">
                              {ev.all_day ? 'Toute la journée' : parseApiDateTime(ev.start_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              {ev.location ? ` · 📍 ${ev.location}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {ev.avatar_emoji && <span style={{ fontSize: 20 }}>{ev.avatar_emoji}</span>}
                            <button onClick={e => deleteOccurrence(ev, e)}
                              title={ev.recurrence && ev.recurrence !== 'aucune' ? 'Supprimer cette occurrence' : 'Supprimer'}
                              style={{ background: 'none', border: 'none', color: '#CCAAAA', fontSize: 15, cursor: 'pointer', padding: '2px 4px', borderRadius: 6, lineHeight: 1 }}>🗑️</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* MODAL FORMULAIRE */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editEvent ? '✏️ Modifier' : '📅 Nouvel événement'}</div>

            <div className="form-group">
              <input className="form-input" placeholder="Titre de l'événement" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <input type="checkbox" id="allday" checked={form.all_day} onChange={e => setForm({ ...form, all_day: e.target.checked })} />
              <label htmlFor="allday" style={{ fontSize: 14, fontWeight: 600, color: '#555577', cursor: 'pointer' }}>Toute la journée</label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Début</label>
                <input type={form.all_day ? 'date' : 'datetime-local'} className="form-input" value={form.start_datetime.slice(0, form.all_day ? 10 : 16)} onChange={e => setForm({ ...form, start_datetime: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Fin</label>
                <input type={form.all_day ? 'date' : 'datetime-local'} className="form-input" value={form.end_datetime.slice(0, form.all_day ? 10 : 16)} onChange={e => setForm({ ...form, end_datetime: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="autre">Autre</option>
                  <option value="ecole">🏫 École</option>
                  <option value="sport">⚽ Sport</option>
                  <option value="medical">💊 Médical</option>
                  <option value="famille">👨‍👩‍👧 Famille</option>
                  <option value="admin">📄 Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Pour qui ?</label>
                <select className="form-input" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })}>
                  <option value="">Toute la famille</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.avatar_emoji} {m.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Lieu</label>
              <input className="form-input" placeholder="Adresse ou lieu..." value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" placeholder="Notes..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>

            <div className="form-group">
              <label className="form-label">Récurrence</label>
              <select className="form-input" value={form.recurrence || 'aucune'} onChange={e => setForm({ ...form, recurrence: e.target.value })}>
                <option value="aucune">Aucune</option>
                <option value="quotidienne">Tous les jours</option>
                <option value="hebdomadaire">Toutes les semaines</option>
                <option value="bimensuelle">Toutes les 2 semaines</option>
                <option value="mensuelle">Tous les mois</option>
              </select>
            </div>

            {editEvent && editEvent.recurrence && editEvent.recurrence !== 'aucune' && (
              <div style={{ marginTop: 10 }}>
                <button type="button" onClick={() => setShowExceptions(!showExceptions)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#6C63FF', padding: 0, marginBottom: 6 }}>
                  🚫 Exceptions ({exceptions.length}) {showExceptions ? '▲' : '▼'}
                </button>
                {showExceptions && (
                  <div style={{ background: '#F8F9FF', borderRadius: 12, padding: '10px 12px' }}>
                    {exceptions.map(ex => (
                      <div key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, flex: 1 }}>{ex.exception_date}{ex.reason ? ` — ${ex.reason}` : ''}</span>
                        <button type="button" onClick={async () => {
                          await api.delete(`/events/${editEvent.id}/exceptions/${ex.exception_date}`);
                          api.get(`/events/${editEvent.id}/exceptions`).then(r => setExceptions(r.data));
                          load();
                        }} style={{ background: 'none', border: 'none', color: '#EF5350', cursor: 'pointer', fontSize: 16 }}>×</button>
                      </div>
                    ))}
                    {!addingException ? (
                      <button type="button" onClick={() => setAddingException(true)}
                        style={{ background: '#EEF0FF', color: '#6C63FF', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}>
                        + Ajouter une exception
                      </button>
                    ) : (
                      <ExceptionPicker
                        eventId={editEvent.id}
                        holidays={holidays}
                        onDone={() => {
                          setAddingException(false);
                          api.get(`/events/${editEvent.id}/exceptions`).then(r => setExceptions(r.data));
                          load();
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              {editEvent && <button className="btn btn-danger btn-sm" onClick={handleDelete} style={{ flex: 1 }}>🗑️ Supprimer</button>}
              <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
